import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryFactory } from 'services/api';
import {
  SystemVariableType,
  UrlType,
  VariableDto,
  VariableType,
} from 'services/api/api-client';
import {
  queriesUrlRegex,
  tokyoUrlRegex,
  VariableAccessLevel,
} from '../variable-modal-data';
import { Loading } from 'components/uikit/suspense/Loading';
import { Field } from 'components/uikit/Field';
import { withStyles } from '@mui/styles';
import styles from './VariableModal.module.scss';
import { VariableDocumentList } from '../variableDocumentsList/VariableDocumentsList';
import { VariableModalSelector } from '../variableModalSelector/VariableModalSelector';
import { VariableTypeSelector } from '../variableTypeSelector/VariableTypeSelector';
import { Input } from 'components/uikit/inputs/Input';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import { FormError } from 'components/uikit/FormError';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { EditDiagramModal } from '../editDiagramModal/EditDiagramModal';
import { Tooltip } from '@mui/material';
import { useModal } from 'components/uikit/modal/useModal';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { Trans } from 'react-i18next';
import {
  accessLevelOptions,
  variableTypeOptions,
} from '../variableTypeSelector/variable-type-options';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import EditIcon from 'assets/icons/edit.svg?react';
import { CheckBox } from 'components/uikit/CheckBox';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { BrightTooltip } from 'components/tooltips/BrightTooltip';
import TipIcon from 'assets/icons/tip_16.svg?react';
import { UrlTypeDropDownInput } from '../urlTypeDropDownInput/UrlTypeDropDownInput';
import { errorToString } from 'helpers/error-helpers.ts';
import { RadioButtonGroup } from 'components/uikit/RadioButtonGroup.tsx';
import { clsx } from 'clsx';

type VariableModalProps = {
  revisionId: number;
  initialDocumentId: number | null;
  documentId?: number;
  isOpen: boolean;
};

type VariableModalCreateProps = {
  mode: 'create';

  onClose: (isSaved: boolean) => void;
  onSubmit: (data: VariableFormData) => Promise<void>;
} & VariableModalProps;

type VariableModalEditProps = {
  mode: 'edit';
  id: number;

  onClose: (isSaved: boolean) => void;
  onSubmit: (data: VariableFormData) => Promise<void>;
} & VariableModalProps;

type VariableModalViewProps = {
  mode: 'view';
  id: number;

  onClose: () => void;
} & VariableModalProps;

export type VariableFormData = Omit<VariableDto, 'id'>;

const DefaultVariable: VariableFormData = {
  name: '',
  actualUrl: null,
  actualValue: {
    value: '',
    variableType: VariableType.Manual,
    hasUpdatedData: false,
    updatedDataDetectedDate: null,
    alternateValue: null,
    urlType: null,
  },
  documents: null,
  systemVariable: null,
  documentId: null,
  lockStatuses: [],
  isEnormous: false,
  isTable: false,
  parameters: null,
};

enum TestCaseIdSource {
  Azure,
  Tokyo,
}

type TestCaseIdRadioButtonOption = {
  text: string;
  value: TestCaseIdSource;
};

export const VariableModal: React.FC<
  VariableModalCreateProps | VariableModalEditProps | VariableModalViewProps
> = (props) => {
  const isEditMode = props.mode === 'edit';
  const isViewMode = props.mode === 'view';
  const isCreateMode = props.mode === 'create';

  const modal = useModal();
  const i18n = useScopedTranslation('Component.EditVariableModal');
  const i18n_updateVariableValue = useScopedTranslation(
    'Component.UpdateVariableModal',
  );
  const i18n_buttonTitle = useScopedTranslation('Button.Title');

  const [isLoading, setIsLoading] = useState(false);
  const [unhandledError, setUnhandledError] = useState<string | null>(null);
  const [defaultValues, setDefaultValues] = useState<VariableFormData>();
  const [selectedAccessLevel, setSelectedAccessLevel] =
    useState<VariableAccessLevel>();
  const [isEditDiagramModalOpen, setIsEditDiagramModalOpen] =
    useState<boolean>(false);

  const variableQuery = QueryFactory.VariableQuery.useGetQuery(
    {
      id: (props as VariableModalEditProps | VariableModalViewProps).id,
      revisionId: props.revisionId,
    },
    {
      enabled: isEditMode || isViewMode,
    },
  );

  useEffect(() => {
    if (variableQuery.data) {
      setDefaultValues(variableQuery.data);
    } else {
      setDefaultValues(DefaultVariable);
    }
  }, [variableQuery.data]);

  useEffect(() => {
    if (props.isOpen) {
      setTimeout(() => {
        form.setFocus('name');
      });
    }
  }, [props.isOpen]);

  useEffect(() => {
    setSelectedAccessLevel(
      (variableQuery.data?.documentId && isEditMode) ||
        (props.documentId && isCreateMode)
        ? VariableAccessLevel.Local
        : VariableAccessLevel.General,
    );
  }, [
    props.documentId,
    isEditMode,
    isCreateMode,
    variableQuery.data?.documentId,
    props.isOpen,
  ]);

  const setExceptionErrors = (e: any) => {
    form.clearErrors();
    setUnhandledError(null);
    const errors = e.response?.errors ?? e.errors;

    if (errors && Object.keys(errors).length > 0) {
      form.setError('name', { type: 'custom', message: errors?.Name });
      form.setError('actualUrl', {
        type: 'custom',
        message: errors?.Url ?? errors?.url,
      });
      form.setError('actualValue.urlType', {
        type: 'custom',
        message: errors?.UrlType,
      });
    } else {
      setUnhandledError(errorToString(e));
    }
  };

  const isVariableUpdateDisabled: {
    disabled: boolean;
    reason?: string;
  } = useMemo(() => {
    if (!variableQuery.data) return { disabled: false };
    if (variableQuery.data.systemVariable?.type === SystemVariableType.Name) {
      return {
        disabled: true,
        reason: i18n.t('forbid_document_name_variable_editing'),
      };
    }
    return { disabled: false };
  }, [variableQuery.data]);

  const form = useAdvancedForm<VariableFormData>(
    useCallback(
      async (data) => {
        if (isViewMode) return;

        setUnhandledError(null);
        setIsLoading(true);

        if (
          isEditMode &&
          variableQuery.data?.documents?.length! > 0 &&
          !props.initialDocumentId
        ) {
          const result = await modal.showConfirm({
            title: i18n_updateVariableValue.t('title'),
            text: (
              <div>
                <p>
                  <Trans
                    i18nKey="Component.UpdateVariableModal.updating_documents"
                    values={{ variableName: variableQuery.data?.name }}
                  />
                </p>
                {variableQuery.data?.documents?.map((doc) => <p>{doc.name}</p>)}
              </div>
            ),
          });
          if (!result) return;
        }

        if (data.actualValue.variableType == VariableType.Manual) {
          data.actualValue.urlType = null;
        }

        data.documentId =
          selectedAccessLevel == VariableAccessLevel.Local
            ? props.initialDocumentId
            : null;

        try {
          await props.onSubmit(data);
        } catch (e) {
          setExceptionErrors(e);
          return;
        } finally {
          setIsLoading(false);
        }

        onModalClose(true);
      },
      [props, variableQuery.data, selectedAccessLevel],
    ),
    {
      defaultValues: defaultValues!,
    },
  );

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (
      isEditMode &&
      form.getValues().actualValue.urlType !==
        form.formState.defaultValues?.actualValue?.urlType
    ) {
      const result = await modal.showConfirm({
        title: i18n.t('ConfirmUrlTypeEdit.title'),
        text: i18n.t('ConfirmUrlTypeEdit.text'),
      });
      if (!result) return;
    }

    await form.handleSubmitDefault(event);
  };

  const onModalClose = (isSaved: boolean) => {
    form.reset();
    form.clearErrors();
    props.onClose(isSaved);
  };

  const documents = variableQuery.data?.documents?.filter(
    (doc) => doc.id != props?.documentId,
  );
  const disableDocumentBinding = documents?.length! > 0;

  const isAutoVariable =
    form.watch('actualValue.variableType') === QueryFactory.VariableType.Auto;

  console.log('isAutoVariable:', isAutoVariable);

  const isEditableUrlTypeSelected =
    form.watch('actualValue.urlType') === UrlType.EditableDgml ||
    form.watch('actualValue.urlType') === UrlType.EditableDot;
  const isCsprojSelected = form.watch('actualValue.urlType') === UrlType.Csproj;
  const isTypeWithLinksSelected =
    form.watch('actualValue.urlType') === UrlType.Tokyo ||
    form.watch('actualValue.urlType') === UrlType.Queries;
  const isTokyoSelected = form.watch('actualValue.urlType') === UrlType.Tokyo;
  const isTypeWithInsertAsTextSelected =
    form.watch('actualValue.urlType') === UrlType.Queries;
  const includeDependencies =
    form.watch('parameters')?.IncludeDependencies === true.toString();
  const includeLinks =
    form.watch('parameters')?.IncludeLinks === true.toString();
  const insertAsText =
    form.watch('parameters')?.InsertAsText === true.toString();
  const useExternalId =
    form.watch('parameters')?.UseExternalId === true.toString();
  const parameters = form.watch('parameters');

  const testCaseIdSourceOptions: TestCaseIdRadioButtonOption[] = useMemo(
    () => [
      { text: i18n.t('TestCaseIdSource.azure'), value: TestCaseIdSource.Azure },
      { text: i18n.t('TestCaseIdSource.tokyo'), value: TestCaseIdSource.Tokyo },
    ],
    [i18n],
  );

  const actualUrl = form.watch('actualUrl');
  useEffect(() => {
    if (!actualUrl) return;

    if (actualUrl.match(tokyoUrlRegex)) {
      form.setValue('actualValue.urlType', UrlType.Tokyo);
    } else if (actualUrl.match(queriesUrlRegex)) {
      form.setValue('actualValue.urlType', UrlType.Queries);
    }
  }, [actualUrl]);

  const urlType = form.watch('actualValue.urlType');
  useEffect(() => {
    // Resetting parameters only in case when user changed url type.
    if (!form.formState.dirtyFields.actualValue?.urlType) return;
    form.setValue('parameters', null);
  }, [urlType]);

  const isSystemVariable = !!variableQuery.data?.systemVariable?.type;

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={() => props.onClose(false)}
      isBlocking={true}
      title={
        isEditMode
          ? i18n.t('edit_modal_title')
          : isViewMode
            ? i18n.t('view_modal_title')
            : i18n.t('add_modal_title')
      }
      classes={{ paper: styles.modal, content: styles.modalContent }}
    >
      <div>
        <Loading
          loading={
            form.formState.isSubmitting || variableQuery.isLoading || isLoading
          }
        >
          <form onSubmit={onSubmit}>
            {!variableQuery.data?.systemVariable?.basedOnDocumentId &&
              props.initialDocumentId && (
                <Field
                  title={i18n.t('binding_document_field')}
                  className={styles.field}
                >
                  <StyledTooltip
                    title={
                      <>
                        <div className={styles.documentsMessage}>
                          {i18n.t('variable_used_documents')}
                        </div>
                        <VariableDocumentList documents={documents} />
                      </>
                    }
                    placement={'top'}
                    disableHoverListener={!disableDocumentBinding}
                  >
                    <div>
                      <VariableModalSelector
                        options={accessLevelOptions}
                        onChange={(selectedOption) =>
                          setSelectedAccessLevel(
                            selectedOption as VariableAccessLevel,
                          )
                        }
                        value={selectedAccessLevel!}
                        disabled={disableDocumentBinding}
                      />
                    </div>
                  </StyledTooltip>
                </Field>
              )}
            {!isSystemVariable && (
              <Field title={i18n.t('type_field')} className={styles.field}>
                <VariableTypeSelector
                  name="actualValue.variableType"
                  form={form}
                  defaultValue={{
                    text: variableQuery.data?.actualValue.variableType!,
                  }}
                  options={variableTypeOptions}
                  disabled={isSystemVariable || isViewMode}
                />
              </Field>
            )}
            <Field title={i18n.t('name_field')} className={styles.field}>
              <Input
                {...form.register('name', { ...requiredRule() })}
                errorText={form.formState.errors.name?.message}
                placeholder={i18n.t('name_placeholder')}
                disabled={isSystemVariable || isViewMode}
              />
            </Field>
            {isAutoVariable ? (
              <>
                <Field title={i18n.t('url_field')} className={styles.field}>
                  <Input
                    key={'url'}
                    {...form.register('actualUrl', { ...requiredRule() })}
                    errorText={form.formState.errors.actualUrl?.message}
                    placeholder={i18n.t('url_placeholder')}
                    disabled={isViewMode}
                  />
                </Field>
                <Field
                  title={i18n.t('url_type_field')}
                  className={styles.field}
                >
                  <UrlTypeDropDownInput
                    name={'actualValue.urlType'}
                    errorMessage={
                      form.formState.errors.actualValue?.urlType?.message
                    }
                    formControl={form.control}
                    placeholder={i18n.t('url_type_placeholder')}
                    disabled={isViewMode}
                  />
                  {(variableQuery.data?.actualValue.urlType ===
                    UrlType.EditableDgml ||
                    variableQuery.data?.actualValue.urlType ===
                      UrlType.EditableDot) &&
                    isEditableUrlTypeSelected && (
                      <TextButton
                        title={i18n.t('edit_diagram')}
                        color={TextButtonColor.Primary}
                        onClick={() => setIsEditDiagramModalOpen(true)}
                        className={styles.deleteButton}
                        icon={<EditIcon />}
                        disabled={isViewMode}
                      />
                    )}
                </Field>
                {isCsprojSelected && (
                  <div
                    className={styles.checkBoxOptionField}
                    onClick={() => {
                      if (isViewMode) return;
                      form.setValue('parameters', {
                        ...parameters,
                        IncludeDependencies: (!includeDependencies).toString(),
                      });
                    }}
                  >
                    <CheckBox
                      className={styles.checkBoxOption}
                      checked={includeDependencies}
                      title={i18n.t('dependencies_checkbox_title')}
                      disabled={isViewMode}
                    />
                    <BrightTooltip
                      title={
                        <label className={styles.checkBoxOptionText}>
                          {i18n.t('dependencies_checkbox_tooltip')}
                        </label>
                      }
                      disableHoverListener={isViewMode}
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -10],
                            },
                          },
                        ],
                      }}
                    >
                      <TipIcon className={styles.csprojTipIcon} />
                    </BrightTooltip>
                  </div>
                )}
                {isTypeWithLinksSelected && (
                  <div className={styles.field}>
                    <div
                      className={styles.checkBoxOptionField}
                      onClick={() => {
                        if (isViewMode) return;
                        form.setValue('parameters', {
                          ...parameters,
                          IncludeLinks: (!includeLinks).toString(),
                        });
                      }}
                    >
                      <CheckBox
                        className={styles.checkBoxOption}
                        checked={includeLinks}
                        title={i18n.t('links_checkbox_title')}
                        disabled={isViewMode}
                      />
                    </div>
                    {isTypeWithInsertAsTextSelected && (
                      <div
                        className={styles.checkBoxOptionField}
                        onClick={() => {
                          if (isViewMode) return;
                          form.setValue('parameters', {
                            ...parameters,
                            InsertAsText: (!insertAsText).toString(),
                          });
                        }}
                      >
                        <CheckBox
                          className={styles.checkBoxOption}
                          checked={insertAsText}
                          title={i18n.t('insert_as_text_checkbox_title')}
                          disabled={isViewMode}
                        />
                      </div>
                    )}
                  </div>
                )}
                {isTokyoSelected && (
                  <RadioButtonGroup<TestCaseIdRadioButtonOption, 'value'>
                    options={testCaseIdSourceOptions}
                    valueField={'value'}
                    labelField={'text'}
                    onSelectedOptionChanged={(option) =>
                      form.setValue('parameters', {
                        ...parameters,
                        UseExternalId: (
                          option.value === TestCaseIdSource.Azure
                        ).toString(),
                      })
                    }
                    rootClassName={clsx(styles.radioButtonGroup, styles.field)}
                    value={
                      useExternalId
                        ? TestCaseIdSource.Azure
                        : TestCaseIdSource.Tokyo
                    }
                    disabled={isViewMode}
                  />
                )}
              </>
            ) : (
              <Field title={i18n.t('value_field')} className={styles.field}>
                <BrightTooltip
                  title={
                    <div className={styles.valueTooltip}>
                      {isVariableUpdateDisabled.reason ?? ''}
                    </div>
                  }
                  disableHoverListener={
                    !isVariableUpdateDisabled.disabled || isViewMode
                  }
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  }}
                >
                  <Input
                    key={'value'}
                    {...form.register('actualValue.value')}
                    errorText={
                      form.formState.errors.actualValue?.value?.message
                    }
                    placeholder={i18n.t('value_placeholder')}
                    disabled={isVariableUpdateDisabled.disabled || isViewMode}
                  />
                </BrightTooltip>
              </Field>
            )}
            <FormError>
              {unhandledError ? unhandledError : form.overallError}
            </FormError>
            {!isViewMode && (
              <div className={styles.footer}>
                <Button
                  className={styles.button}
                  color={ButtonColor.Secondary}
                  title={i18n_buttonTitle.t('cancel')}
                  onClick={() => onModalClose(false)}
                />
                <Button
                  className={styles.button}
                  color={ButtonColor.Default}
                  type={'submit'}
                  title={i18n_buttonTitle.t('save')}
                />
              </div>
            )}
          </form>
        </Loading>

        {isEditMode && (
          <>
            <EditDiagramModal
              isOpen={isEditDiagramModalOpen}
              setIsOpen={setIsEditDiagramModalOpen}
              xml={
                form.getValues().actualValue?.alternateValue ??
                variableQuery.data?.actualValue.alternateValue
              }
              form={form}
            />
          </>
        )}
      </div>
    </CustomModal>
  );
};

const StyledTooltip = withStyles({
  tooltipPlacementTop: {
    marginBottom: '4px !important',
  },
  tooltip: {
    backgroundColor: '#ffffff',
    color: '#000000',
    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.12)',
    borderRadius: '2px',
    fontSize: 14,
    padding: '8px 12px 8px 12px',
    wordWrap: 'break-word',
    maxWidth: '700px',
  },
})(Tooltip);
